import { Colors, Lightning } from '@lightningjs/sdk';
import { translate } from 'support/translate';
import { HoverableComponent } from 'components/common/HoverableComponent';
import { constants } from 'aliases';
import { passwordSchema } from 'models/input/validationSchema';
import { getFocusDepth, getImageTextureObj } from 'support/generalUtils';
import FocusableIconToggle, {
  StatefulIcon,
} from 'components/common/FocusableIconToggle';
import LoginInputField from 'components/common/input/LoginInputField';
import ActionsList from 'components/common/ActionsList';
import { LoginRadiusError } from 'types/loginRadius';

export interface PasswordInputFieldTemplateSpec
  extends Lightning.Component.TemplateSpec {
  allowValidationError: boolean;
  loginFailedError: LoginRadiusError;
  ActionsList: typeof ActionsList;
}

const TOGGLE_Y = 42;
const TOGGLE_WIDTH = 49;
const TOGGLE_HEIGHT = 44;
const TOGGLE_HIDE_ICON = getImageTextureObj(
  'static/images/password-hide.svg',
  20,
  20,
);
const TOGGLE_SHOW_ICON = getImageTextureObj(
  'static/images/password-show.svg',
  20,
  20,
);
const TOGGLE_ICONS: StatefulIcon = {
  selected: TOGGLE_HIDE_ICON,
  unselected: TOGGLE_SHOW_ICON,
};
const INITIAL_PASSWORD_MODE = true;

const SPACING = 8;

export default class PasswordInputField
  extends HoverableComponent<PasswordInputFieldTemplateSpec>
  implements
    Lightning.Component.ImplementTemplateSpec<PasswordInputFieldTemplateSpec>
{
  private _ActionsList = this.getByRef('ActionsList')!;
  private _InputField = this._ActionsList.getItemByRef(
    'InputField',
  ) as LoginInputField;
  private _BaseInputField = this._InputField.inputField;
  private _TogglePasswordIcon = this._ActionsList.getItemByRef(
    'TogglePasswordIcon',
  ) as FocusableIconToggle;
  private _passwordMode = INITIAL_PASSWORD_MODE;

  get input() {
    return this._InputField.input;
  }

  set allowValidationError(allow: boolean) {
    this._InputField.schema = allow ? passwordSchema : undefined;
  }

  set loginFailedError(error: LoginRadiusError) {
    this.showFieldLevelError(error);
  }

  static override _template(): Lightning.Component.Template<PasswordInputFieldTemplateSpec> {
    return {
      flex: {
        direction: 'column',
      },
      ActionsList: {
        type: ActionsList,
        flex: {
          direction: 'row',
        },
        passSignals: {
          $openKeyboard: true,
        },
        signals: {
          $togglePassword: true,
          $onHover: true,
        },
        spacing: SPACING,
        items: [
          {
            ref: 'InputField',
            type: LoginInputField,
            placeholderText: translate(
              'account.login.passwordPlaceholderLabel',
            ),
            showChangeText: false,
            schema: passwordSchema,
            passSignals: {
              $onHover: true,
              $loginInputSelected: '$openKeyboard',
            },
            signals: { $onHover: true },
            zIndex: constants.ui.zIndex.foregroundLayer + 2,
            errorType: translate('account.login.passwordErrorType'),
            errorMessage: translate('account.login.invalidPasswordError'),
          },
          {
            ref: 'TogglePasswordIcon',
            type: FocusableIconToggle,
            y: TOGGLE_Y, // Manually position because of the dynamic re-sizing of the error display
            mountY: 0.5,
            w: TOGGLE_WIDTH,
            h: TOGGLE_HEIGHT,
            selected: !INITIAL_PASSWORD_MODE,
            zIndex: constants.ui.zIndex.foregroundLayer + 2,
            backgroundColors: {
              focused: Colors('loginTogglePassword').get(),
              unfocused: Colors('transparent').get(),
            },
            icons: TOGGLE_ICONS,
            action: '$togglePassword',
            passSignals: {
              $togglePassword: true,
              $onHover: true,
            },
            signals: {
              $onHover: true,
            },
          },
        ],
      },
    };
  }

  override _init() {
    this._BaseInputField.patch({
      passwordMode: this._passwordMode,
      passwordMask: translate('account.login.keyboardMask'),
    });

    this.patch({
      w: this._InputField.w + this._TogglePasswordIcon.w + SPACING,
    });
  }

  override _active() {
    this.updateToggleTts(true);
  }

  private $togglePassword() {
    this._passwordMode = !this._passwordMode;
    const isHidden = this._passwordMode;

    this._BaseInputField.patch({
      passwordMode: this._passwordMode,
    });
    this._BaseInputField._update();

    this.updateToggleTts(isHidden);

    this.fireAncestors(
      '$announcerRefresh',
      getFocusDepth(this.application, this._TogglePasswordIcon),
    );
  }

  private updateToggleTts(isHidden = true) {
    const toggleType = isHidden
      ? translate('ttsPrompts.password.toggleHidden')
      : translate('ttsPrompts.password.toggleShown');

    this._TogglePasswordIcon.title = translate(
      'ttsPrompts.password.toggle',
      toggleType,
    );
  }

  override _unfocus() {
    this._ActionsList.index = 0;
  }

  async checkValid() {
    return await this._InputField.checkValid();
  }

  override _getFocused() {
    return this._ActionsList;
  }

  showFieldLevelError(error: LoginRadiusError) {
    this._InputField.showFieldLevelError(error);
  }
}
